import type { AuthUser } from '../auth-store';
import { allRows, deleteRow, getRow, kvDelete, kvGet, kvSet, nextCounter, putRow } from './db';
import { hashPin, MAX_PIN_ATTEMPTS, PIN_LOCK_MS, pinMatches } from './pin';
import { useOfflineStore } from './state';
import type { OfflinePack, OfflineUser, QueueRecord } from './types';

/**
 * What is on this device, with the pack also held in memory: the till reads it
 * on every keystroke of a member search.
 */
let pack: OfflinePack | null = null;

export const currentPack = () => pack;

export async function savePack(next: OfflinePack): Promise<void> {
  await kvSet('pack', next);
  pack = next;
  useOfflineStore.getState().set({ hasPack: true, packAt: next.downloadedAt });
}

export async function clearPack(): Promise<void> {
  await kvDelete('pack');
  pack = null;
  useOfflineStore.getState().set({ hasPack: false, packAt: null });
}

export async function persistPackChange(next: OfflinePack): Promise<void> {
  pack = next;
  await kvSet('pack', next);
}

export async function deviceId(): Promise<string> {
  const existing = await kvGet<string>('deviceId');
  if (existing) return existing;
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  const id = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  await kvSet('deviceId', id);
  return id;
}

export const nextSaleSeq = () => nextCounter('saleSeq');

export const queueRecords = () => allRows<QueueRecord>('queue');
export const queuedRecord = (id: string) => getRow<QueueRecord>('queue', id);

async function refreshCounts(): Promise<void> {
  const rows = await queueRecords();
  useOfflineStore.getState().set({
    pending: rows.length,
    stuck: rows.filter((r) => !!r.lastError).length,
  });
}

export async function enqueue(record: QueueRecord): Promise<void> {
  await putRow('queue', record);
  await refreshCounts();
}

export async function dequeue(id: string): Promise<void> {
  await deleteRow('queue', id);
  await refreshCounts();
}

export async function markStuck(record: QueueRecord, error: string): Promise<void> {
  await putRow('queue', { ...record, lastError: error });
  await refreshCounts();
}

export const offlineUsers = () => allRows<OfflineUser>('pins');
export const offlineUser = (userId: string) => getRow<OfflineUser>('pins', userId);

export async function setTillPin(profile: AuthUser, pin: string): Promise<void> {
  await putRow('pins', {
    userId: profile.id,
    profile,
    ...(await hashPin(pin)),
    failedAttempts: 0,
    lockedUntil: 0,
  } satisfies OfflineUser);
}

/** Keep the stored name, roles and branch fresh without touching the PIN. */
export async function refreshPinProfile(profile: AuthUser): Promise<void> {
  const row = await offlineUser(profile.id);
  if (row) await putRow('pins', { ...row, profile });
}

export type UnlockResult =
  | { ok: true; profile: AuthUser }
  | { ok: false; reason: 'wrong' | 'locked' | 'unknown'; lockedUntil?: number };

export async function unlockWithPin(userId: string, pin: string): Promise<UnlockResult> {
  const row = await offlineUser(userId);
  if (!row) return { ok: false, reason: 'unknown' };
  if (row.lockedUntil > Date.now()) {
    return { ok: false, reason: 'locked', lockedUntil: row.lockedUntil };
  }
  if (await pinMatches(pin, row)) {
    await putRow('pins', { ...row, failedAttempts: 0, lockedUntil: 0 });
    return { ok: true, profile: row.profile };
  }
  const failedAttempts = row.failedAttempts + 1;
  const locked = failedAttempts >= MAX_PIN_ATTEMPTS;
  const lockedUntil = locked ? Date.now() + PIN_LOCK_MS : 0;
  await putRow('pins', { ...row, failedAttempts: locked ? 0 : failedAttempts, lockedUntil });
  return locked ? { ok: false, reason: 'locked', lockedUntil } : { ok: false, reason: 'wrong' };
}

let booted: Promise<void> | null = null;

/** Load what is on the device. Every request waits for this before routing. */
export function bootOfflineStore(): Promise<void> {
  if (!booted) {
    booted = (async () => {
      try {
        pack = (await kvGet<OfflinePack>('pack')) ?? null;
        useOfflineStore.getState().set({ hasPack: !!pack, packAt: pack?.downloadedAt ?? null });
        await refreshCounts();
      } catch {
        // No IndexedDB (a locked-down browser): the till simply works online only.
        pack = null;
      }
    })();
  }
  return booted;
}
